import React, { Dispatch, SetStateAction, useState } from 'react';
import DiscoveryPlatformsFilter from './DiscoveryPlatformsFilter';
import { Button, Col, DropdownButton, Form, Image, Row } from 'react-bootstrap';
import _debounce from 'lodash/debounce';
import { TextField } from '@mui/material';
import {
    AudienceAge,
    AudienceGender,
    audiencePercentages,
    DEFAULT_AUDIENCE_WEIGHT,
    engagementRateArr,
    fakeFollowersArr,
    growthInterval,
    growthOperator,
    growthValue,
    IDiscoveryFilterPlatform,
    IUiDefaultPlatforms,
    lastPost,
    MoodashActivePlatforms,
    MoodashGender,
} from '../dto/IDiscoveryFilter';
import { CustomAutocomplete } from '../../../../../shared/components/Autocomplete';
import { IMoodashFilterState } from '../../../../../shared/hooks/useMoodashData';
import { FiInfo } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import TooltipOverlay from '../../../../../shared/components/tooltip/Tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { IAllStates } from '../../../../../store/rootReducer';
import { openUpgradeModal } from '../../../../../store/subscription/subscriptionSlice';

interface IFiltersProps {
    filters: IDiscoveryFilterPlatform;
    setFilters: React.Dispatch<React.SetStateAction<IDiscoveryFilterPlatform>>;
    fetchDiscovery: () => void;
    setMoodashFilter: React.Dispatch<React.SetStateAction<IMoodashFilterState>>;
    moodashFilters: IMoodashFilterState;
    uiDefaults: IUiDefaultPlatforms;
    setUiDefaults: React.Dispatch<React.SetStateAction<IUiDefaultPlatforms>>;
    interests: never[];
    locations: never[];
    audienceLocations: never[];
    languages: never[];
    topics: never[];
    hashtags: never[];
    users: never[];
    mentions: never[];
    setResetKey: React.Dispatch<React.SetStateAction<number>>;
    resetKey: number;
    clearFilterAction: () => void;
    setSelectedPresetOption: Dispatch<SetStateAction<number | null>>;
    selectedPresetOption: number | null;
}

type ParamValue = string | string[] | number | AutocompleteItem | AutocompleteItem[];

interface AutocompleteItem {
    type: string;
    value: string;
}

const DiscoveryFilter = ({
    filters,
    setFilters,
    fetchDiscovery,
    setMoodashFilter,
    uiDefaults,
    setUiDefaults,
    interests,
    users,
    locations,
    languages,
    topics,
    hashtags,
    moodashFilters,
    mentions,
    audienceLocations,
    resetKey,
    clearFilterAction,
    setSelectedPresetOption,
    selectedPresetOption,
}: IFiltersProps) => {
    const [isInfluencerFilterShown, setIsInfluencerFilterShown] = useState(true);
    const [isAudienceFilterShown, setIsAudienceFilterShown] = useState(true);
    const isDiscoveryLimited = useSelector((state: IAllStates) => state.auth.user.company?.package?.limitDiscovery);
    const isInstagramActive = MoodashActivePlatforms.INSTAGRAM === moodashFilters.platform;
    const activePlatform = moodashFilters.platform.toLowerCase() as keyof IDiscoveryFilterPlatform;
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const openUpgradeModalAction = () => {
        if (isDiscoveryLimited) {
            dispatch(
                openUpgradeModal({
                    source: 'filters',
                })
            );
        }
    };
    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, path: string) => {
        if (isDiscoveryLimited) {
            return openUpgradeModalAction();
        }
        if (!!selectedPresetOption) {
            setSelectedPresetOption(null);
        }
        const { value } = event.target;
        setFilters(prevState => {
            const keys = path.split('.');
            const newState: any = { ...prevState };
            const currentPlatformFilter = { ...(newState[activePlatform] || {}) };

            let nested = currentPlatformFilter;
            for (let i = 0; i < keys.length - 1; i++) {
                const key = keys[i];
                nested[key] = { ...nested[key] };
                nested = nested[key];
            }

            const lastKey = keys[keys.length - 1];
            nested[lastKey] = value;

            newState[activePlatform] = currentPlatformFilter;

            return newState;
        });
    };

    const adjustParamValues = (paramKey: string, paramValue: any) => {
        switch (paramKey) {
            case 'influencer.textTags.hashtags':
                return paramValue?.map((item: string) => ({ type: 'hashtag', value: item }));
            case 'influencer.textTags.mentions':
                return paramValue?.map((item: any) => ({ type: 'mention', value: item?.username }));
            case 'influencer.relevance.topics':
                return paramValue?.map((item: any) => `#${item}`);
            case 'influencer.relevance.usernames':
                return paramValue?.map((item: any) => `@${item.username}`);
            case 'influencer.location':
            case 'audience.location':
            case 'influencer.interests':
                return paramValue?.map((value: { id: number }) => value.id);
            case 'influencer.language':
            case 'audience.language.id':
                return paramValue?.code;
            case 'audience.age':
                return paramValue?.map((item: string) => ({ id: item, weight: DEFAULT_AUDIENCE_WEIGHT }));
            default:
                return paramValue;
        }
    };

    const changeAutocompleteHandler = _debounce((paramKey: string, paramValue: ParamValue) => {
        if (isDiscoveryLimited) {
            return openUpgradeModalAction();
        }
        if (!!selectedPresetOption) {
            setSelectedPresetOption(null);
        }
        const adjustedValues = adjustParamValues(paramKey, paramValue);
        const keys = paramKey.split('.');
        const lastKey = keys[keys.length - 1];
        const beforeLastKey = keys[keys.length - 2];

        setUiDefaults(prevState => {
            const newState = { ...prevState };

            const currentPlatformFilter = { ...(newState[activePlatform] || {}) };

            let nested = currentPlatformFilter;

            const existsSpecificObject = keys.includes('textTags') || keys.includes('relevance');

            for (let i = 0; i < keys.length - (existsSpecificObject ? 2 : 1); i++) {
                const key = keys[i];
                nested[key] = { ...nested[key] };
                nested = nested[key];
            }
            nested[lastKey] = paramValue;
            newState[activePlatform] = currentPlatformFilter;
            return newState;
        });

        setFilters(prevState => {
            const newState = { ...prevState };

            const currentPlatformFilter = { ...(newState[activePlatform] || {}) };

            let nested = currentPlatformFilter;

            const existsSpecificObject = keys.includes('textTags') || keys.includes('relevance');

            // Traverse to the correct nested object
            for (let i = 0; i < keys.length - (existsSpecificObject ? 2 : 1); i++) {
                const key = keys[i];
                nested[key] = { ...nested[key] };
                nested = nested[key];
            }

            const isTextTagsArray = Array.isArray(adjustedValues) && paramKey.includes('textTags');

            const isRelevanceArray = Array.isArray(adjustedValues) && paramKey.includes('relevance');
            // && paramValue.length > 0;

            const isSimpleArray = Array.isArray(adjustedValues) && !paramKey.includes('relevance');
            const isAppendingToExistingArray = Array.isArray(nested[lastKey]);

            // 1. Autocomplete array (objects with type/value)
            if (isTextTagsArray) {
                let currentType: string;
                if (lastKey === 'mentions') {
                    currentType = 'mention';
                } else if (lastKey === 'hashtags') {
                    currentType = 'hashtag';
                }
                const existingArr = Array.isArray(nested[beforeLastKey]) ? nested[beforeLastKey] : [];
                const filteredArr = existingArr.filter((item: AutocompleteItem) => item.type !== currentType);
                const mergedArr = [...filteredArr, ...adjustedValues];
                nested[beforeLastKey] = mergedArr;

                // 2. Simple array (not autocomplete, not relevance)
            } else if (isSimpleArray) {
                nested[lastKey] = Array.from(new Set(adjustedValues as (string | number)[]));

                // 3. Relevance-related strings
            } else if (isRelevanceArray) {
                let currentType: string;
                if (lastKey === 'topics') {
                    currentType = '#';
                } else if (lastKey === 'usernames') {
                    currentType = '@';
                }
                const existingArr = Array.isArray(nested['relevance']) ? nested['relevance'] : [];
                const filteredArr = existingArr.filter(
                    (item: string) => typeof item === 'string' && item.charAt(0) !== currentType
                );
                nested['relevance'] = [...filteredArr, ...adjustedValues];
                // 4. Single autocomplete object
            } else if (isAppendingToExistingArray) {
                nested[lastKey] = Array.from(new Set([...nested[lastKey], adjustedValues]));
                // 6. Fallback assignment
            } else if (lastKey === 'followersGrowthRate') {
                nested[lastKey] = { ...nested[lastKey], value: adjustedValues, operator: growthOperator.GT };
            } else {
                nested[lastKey] = adjustedValues;
            }
            newState[activePlatform] = currentPlatformFilter;
            return newState;
        });
    }, 200);

    function getGrowthNameByValue(value?: number): string | undefined {
        const match = growthValue.find(item => item.value === value);
        return match?.name ?? '';
    }

    const submitDiscoveryFilterForm = (event: React.FormEvent) => {
        event.preventDefault();
        if (fetchDiscovery) {
            fetchDiscovery();
        }
        // setIsInfluencerFilterShown(false);
        // setIsAudienceFilterShown(false);
    };

    const handleNumericChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, path: string) => {
        const sanitized = event.target.value.replace(/[^0-9]/g, '');
        handleFilterChange({ ...event, target: { ...event.target, value: sanitized } }, path);
    };

    return (
        <Form
            onSubmit={submitDiscoveryFilterForm}
            onKeyDown={e => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                }
            }}
        >
            <div className="my-3">
                <DiscoveryPlatformsFilter setMoodashFilterParams={setMoodashFilter} moodashFilters={moodashFilters} />
            </div>

            <div
                className={`d-flex justify-content-between align-items-center ${
                    isInfluencerFilterShown ? 'mb-3' : ''
                } `}
            >
                <h5 className="mb-0">{t('brand.campaign.discovery.influencer_filters')}</h5>
                <Image
                    src="/icons/img-double-down.png"
                    alt="Arrow down img"
                    className={`${isInfluencerFilterShown && 'rotate-180'} rotate-smooth cursor-pointer`}
                    height={15}
                    onClick={() => setIsInfluencerFilterShown(prevState => !prevState)}
                />
            </div>
            {isInfluencerFilterShown && (
                <Row
                    onClick={event => {
                        event.stopPropagation();
                        openUpgradeModalAction();
                    }}
                >
                    <Col lg={3} md={4} className="mb-2">
                        <CustomAutocomplete
                            key={`location-${activePlatform}-${resetKey}`}
                            dataArray={locations}
                            defaultArray={uiDefaults?.[activePlatform]?.influencer?.location ?? []}
                            isMultiple={true}
                            isWithImage={false}
                            inputLabel="discovery.location"
                            queryFilterKey="influencer.location"
                            inputLabelTranslation={`${t('brand.campaign.discovery.influencer_location')}`}
                            handleChangeParams={changeAutocompleteHandler}
                            changeEventKey="locations"
                            setFilterState={setMoodashFilter}
                            disableClientFilter={true}
                            isDisabled={isDiscoveryLimited}
                        />
                    </Col>
                    <Col lg={3} md={4} className="mb-2">
                        <DropdownButton
                          className={`dropdown-secondary location-dropdown ${
                            filters?.[activePlatform]?.influencer?.followers?.min ? 'active' : ''
                          }`}
                          title={`${
                            isInstagramActive
                              ? t('brand.campaign.discovery.follower_range')
                              : t('brand.campaign.discovery.subscriber_range')
                          }
                            ${filters?.[activePlatform]?.influencer?.followers?.min ?? ''} ${
                            filters?.[activePlatform]?.influencer?.followers?.min ? '-' : ''
                          } ${filters?.[activePlatform]?.influencer?.followers?.max ?? ''}`}
                        >
                            <Row>
                                <Col md={6}>
                                    <TextField
                                      key={`followers.min-${resetKey}`}
                                      label={t('brand.campaign.book.offer.filters.from')}
                                      variant="outlined"
                                      value={filters?.[activePlatform]?.influencer?.followers?.min ?? ''}
                                      size="small"
                                      name="min"
                                      type="number"
                                      onChange={event => handleNumericChange(event, 'influencer.followers.min')}
                                      className="w-100"
                                      InputProps={{ inputProps: { min: 0 } }}
                                      onKeyDown={event =>
                                        ['-', '+', 'e', 'E', '.'].includes(event.key) && event.preventDefault()
                                      }
                                      disabled={isDiscoveryLimited}
                                    />
                                </Col>
                                <Col md={6}>
                                    <TextField
                                      key={`followers.max-${resetKey}`}
                                      label={t('brand.campaign.book.offer.filters.to')}
                                      variant="outlined"
                                      onChange={event => handleNumericChange(event, 'influencer.followers.max')}
                                      size="small"
                                      type="number"
                                      name="max"
                                      value={filters?.[activePlatform]?.influencer?.followers?.max ?? ''}
                                      className="w-100"
                                      InputProps={{ inputProps: { min: 0 } }}
                                      onKeyDown={event =>
                                        ['-', '+', 'e', 'E', '.'].includes(event.key) && event.preventDefault()
                                      }
                                      disabled={isDiscoveryLimited}
                                    />
                                </Col>
                            </Row>
                        </DropdownButton>
                    </Col>
                    {moodashFilters.platform === MoodashActivePlatforms.INSTAGRAM ? (
                        <Col lg={3} md={4} className="mb-2">
                            <DropdownButton
                                className={`dropdown-secondary location-dropdown ${
                                    filters?.[activePlatform]?.influencer?.reelsPlays?.min ? 'active' : ''
                                }`}
                                title={`${t('brand.campaign.discovery.average_views_range')}
                            ${filters?.[activePlatform]?.influencer?.reelsPlays?.min ?? ''} ${
                                    filters?.[activePlatform]?.influencer?.reelsPlays?.min ? '-' : ''
                                } ${filters?.[activePlatform]?.influencer?.reelsPlays?.max ?? ''}`}
                            >
                                <Row>
                                    <Col md={6}>
                                        <TextField
                                            key={`reelsPlays.min-${resetKey}`}
                                            label={t('brand.campaign.book.offer.filters.from')}
                                            value={filters?.[activePlatform]?.influencer?.reelsPlays?.min ?? ''}
                                            variant="outlined"
                                            size="small"
                                            name="min"
                                            type="number"
                                            onChange={event => handleNumericChange(event, 'influencer.reelsPlays.min')}
                                            className="w-100"
                                            InputProps={{ inputProps: { min: 0 } }}
                                            onKeyDown={event =>
                                                ['-', '+', 'e', 'E', '.'].includes(event.key) && event.preventDefault()
                                            }
                                            disabled={isDiscoveryLimited}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <TextField
                                            key={`reelsPlays.max-${resetKey}`}
                                            value={filters?.[activePlatform]?.influencer?.reelsPlays?.max ?? ''}
                                            label={t('brand.campaign.book.offer.filters.to')}
                                            variant="outlined"
                                            onChange={event => handleNumericChange(event, 'influencer.reelsPlays.max')}
                                            size="small"
                                            type="number"
                                            name="max"
                                            className="w-100"
                                            InputProps={{ inputProps: { min: 0 } }}
                                            onKeyDown={event =>
                                                ['-', '+', 'e', 'E', '.'].includes(event.key) && event.preventDefault()
                                            }
                                            disabled={isDiscoveryLimited}
                                        />
                                    </Col>
                                </Row>
                            </DropdownButton>
                        </Col>
                    ) : (
                        <Col lg={3} md={4} className="mb-2">
                            <DropdownButton
                                className={`dropdown-secondary location-dropdown ${
                                    filters?.[activePlatform]?.influencer?.views?.min ? 'active' : ''
                                }`}
                                title={`${t('brand.campaign.discovery.average_views_range')}
                        ${filters?.[activePlatform]?.influencer?.views?.min ?? ''} ${
                                    filters?.[activePlatform]?.influencer?.views?.min ? '-' : ''
                                } ${filters?.[activePlatform]?.influencer?.views?.max ?? ''}`}
                            >
                                <Row>
                                    <Col md={6}>
                                        <TextField
                                            key={`views.min-${resetKey}`}
                                            label={t('brand.campaign.book.offer.filters.from')}
                                            variant="outlined"
                                            value={filters?.[activePlatform]?.influencer?.views?.min ?? ''}
                                            size="small"
                                            name="min"
                                            type="number"
                                            onChange={event => handleFilterChange(event, 'influencer.views.min')}
                                            className="w-100"
                                            InputProps={{ inputProps: { min: 0 } }}
                                            onKeyPress={event =>
                                                (event?.key === '-' || event?.key === '+') && event.preventDefault()
                                            }
                                            disabled={isDiscoveryLimited}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <TextField
                                            key={`views.max-${resetKey}`}
                                            label={t('brand.campaign.book.offer.filters.to')}
                                            variant="outlined"
                                            onChange={event => handleFilterChange(event, 'influencer.views.max')}
                                            size="small"
                                            type="number"
                                            name="max"
                                            value={filters?.[activePlatform]?.influencer?.views?.max ?? ''}
                                            className="w-100"
                                            InputProps={{ inputProps: { min: 0 } }}
                                            onKeyPress={event =>
                                                (event?.key === '-' || event?.key === '+') && event.preventDefault()
                                            }
                                            disabled={isDiscoveryLimited}
                                        />
                                    </Col>
                                </Row>
                            </DropdownButton>
                        </Col>
                    )}
                    <Col lg={3} md={4} className="mb-2">
                        <DropdownButton
                            className={`dropdown-secondary location-dropdown ${
                                filters?.[activePlatform]?.influencer?.age?.min ? 'active' : ''
                            }`}
                            title={`${t('brand.campaign.discovery.age')}
                        ${filters?.[activePlatform]?.influencer?.age?.min ?? ''} ${
                                filters?.[activePlatform]?.influencer?.age?.min ? '-' : ''
                            } ${filters?.[activePlatform]?.influencer?.age?.max ?? ''}`}
                        >
                            <Row>
                                <Col md={6}>
                                    <TextField
                                        key={`age.min-${resetKey}`}
                                        label={t('brand.campaign.discovery.from')}
                                        variant="outlined"
                                        size="small"
                                        name="min"
                                        value={filters?.[activePlatform]?.influencer?.age?.min ?? ''}
                                        type="number"
                                        onChange={event => handleNumericChange(event, 'influencer.age.min')}
                                        className="w-100"
                                        InputProps={{ inputProps: { min: 0 } }}
                                        onKeyDown={event =>
                                            ['-', '+', 'e', 'E', '.'].includes(event.key) && event.preventDefault()
                                        }
                                        disabled={isDiscoveryLimited}
                                    />
                                </Col>
                                <Col md={6}>
                                    <TextField
                                        label={t('brand.campaign.discovery.to')}
                                        key={`age.max-${resetKey}`}
                                        variant="outlined"
                                        onChange={event => handleNumericChange(event, 'influencer.age.max')}
                                        size="small"
                                        value={filters?.[activePlatform]?.influencer?.age?.max ?? ''}
                                        type="number"
                                        name="max"
                                        className="w-100"
                                        InputProps={{ inputProps: { min: 0 } }}
                                        onKeyDown={event =>
                                            ['-', '+', 'e', 'E', '.'].includes(event.key) && event.preventDefault()
                                        }
                                        disabled={isDiscoveryLimited}
                                    />
                                </Col>
                            </Row>
                        </DropdownButton>
                    </Col>
                    <Col lg={3} md={4} className="mb-2 mb-md-0">
                        <DropdownButton
                            className={`dropdown-secondary location-dropdown ${
                                filters?.[activePlatform]?.influencer?.followersGrowthRate?.value ? 'active' : ''
                            }`}
                            title={`${
                                isInstagramActive
                                    ? t('brand.campaign.discovery.growth_followers')
                                    : t('brand.campaign.discovery.growth_subscribers')
                            }:
                        ${
                            getGrowthNameByValue(filters?.[activePlatform]?.influencer?.followersGrowthRate?.value) ??
                            ''
                        } ${
                                filters?.[activePlatform]?.influencer?.followersGrowthRate?.interval
                                    ? `- ${t('brand.campaign.discovery.period')}:`
                                    : ''
                            } ${
                                filters?.[activePlatform]?.influencer?.followersGrowthRate?.interval
                                    ? t(
                                          `brand.campaign.discovery.growthInterval.${filters?.[activePlatform]?.influencer?.followersGrowthRate?.interval}`
                                      )
                                    : ''
                            }`}
                        >
                            <Row>
                                <Col md={6}>
                                    <CustomAutocomplete
                                        key={`growth-${activePlatform}-${resetKey}`}
                                        dataArray={growthValue}
                                        defaultArray={
                                            growthValue.find(
                                                item =>
                                                    item.value ===
                                                    filters?.[activePlatform]?.influencer?.followersGrowthRate?.value
                                            ) ?? ''
                                        }
                                        isMultiple={false}
                                        isWithImage={false}
                                        inputLabel="growthRate.valuePercentage"
                                        queryFilterKey="influencer.followersGrowthRate"
                                        inputLabelTranslation={`${t('brand.campaign.discovery.growth')}`}
                                        handleChangeParams={changeAutocompleteHandler}
                                        isDisabled={isDiscoveryLimited}
                                    />
                                </Col>
                                <Col md={6}>
                                    <CustomAutocomplete
                                        key={`interval-${activePlatform}-${resetKey}`}
                                        dataArray={Object.values(growthInterval)}
                                        defaultArray={
                                            Object.values(growthInterval).find(
                                                item =>
                                                    item ===
                                                    filters?.[activePlatform]?.influencer?.followersGrowthRate?.interval
                                            ) ?? ''
                                        }
                                        isMultiple={false}
                                        isWithImage={false}
                                        inputLabel="growthRate.interval"
                                        queryFilterKey="influencer.followersGrowthRate.interval"
                                        inputLabelTranslation={`${t('brand.campaign.discovery.period')}`}
                                        handleChangeParams={changeAutocompleteHandler}
                                        isDisabled={isDiscoveryLimited}
                                    />
                                </Col>
                            </Row>
                        </DropdownButton>
                    </Col>
                    <Col lg={3} md={4} className="mb-2 mb-md-0">
                        <CustomAutocomplete
                            key={`languages-${activePlatform}-${resetKey}`}
                            dataArray={languages}
                            isMultiple={false}
                            defaultArray={uiDefaults?.[activePlatform]?.influencer?.language ?? ''}
                            isWithImage={false}
                            inputLabel="language.name"
                            queryFilterKey="influencer.language"
                            inputLabelTranslation={`${t('brand.campaign.discovery.language')}`}
                            handleChangeParams={changeAutocompleteHandler}
                            changeEventKey="languages"
                            setFilterState={setMoodashFilter}
                            disableClientFilter={true}
                            isDisabled={isDiscoveryLimited}
                        />
                    </Col>
                    <Col lg={2} md={4} className="mb-2 mb-md-0">
                        <CustomAutocomplete
                            key={`engagementRate-${activePlatform}-${resetKey}`}
                            dataArray={engagementRateArr}
                            defaultArray={
                                engagementRateArr.find(
                                    item => item.value === filters?.[activePlatform]?.influencer?.engagementRate
                                ) ?? []
                            }
                            isMultiple={false}
                            isWithImage={false}
                            inputLabel="audience.credibility"
                            queryFilterKey="influencer.engagementRate"
                            inputLabelTranslation={`${t('brand.campaign.discovery.engagement_rate')}`}
                            handleChangeParams={changeAutocompleteHandler}
                            isDisabled={isDiscoveryLimited}
                        />
                    </Col>
                    <Col lg={2} md={4} className="mb-2 mb-md-0">
                        <CustomAutocomplete
                            key={`influencer_gender-${activePlatform}-${resetKey}`}
                            dataArray={Object.values(MoodashGender)}
                            defaultArray={
                                Object.values(MoodashGender).find(
                                    item => item === filters?.[activePlatform]?.influencer?.gender
                                ) ?? []
                            }
                            isMultiple={false}
                            isWithImage={false}
                            inputLabel="moodash_gender"
                            queryFilterKey="influencer.gender"
                            inputLabelTranslation={`${t('brand.campaign.discovery.gender')}`}
                            handleChangeParams={changeAutocompleteHandler}
                            isDisabled={isDiscoveryLimited}
                        />
                    </Col>
                    <Col lg={2} md={4} className="mb-2 mb-md-0">
                        <CustomAutocomplete
                            key={`last-posted-${activePlatform}-${resetKey}`}
                            dataArray={lastPost}
                            defaultArray={
                                lastPost.find(item => item === filters?.[activePlatform]?.influencer?.lastposted) ?? []
                            }
                            isMultiple={false}
                            isWithImage={false}
                            inputLabel="last_posted"
                            queryFilterKey="influencer.lastposted"
                            inputLabelTranslation={`${t('brand.campaign.discovery.last_post')}`}
                            handleChangeParams={changeAutocompleteHandler}
                            isDisabled={isDiscoveryLimited}
                        />
                    </Col>
                    {[MoodashActivePlatforms.INSTAGRAM].includes(moodashFilters.platform) && (
                        <Col lg={3} md={4} className="mt-2">
                            <div className="d-flex align-items-center">
                                <CustomAutocomplete
                                    key={`categories-${activePlatform}-${resetKey}`}
                                    dataArray={interests}
                                    defaultArray={uiDefaults?.[activePlatform]?.influencer?.interests ?? []}
                                    isMultiple={true}
                                    isWithImage={false}
                                    inputLabel="influencer.interests"
                                    queryFilterKey="influencer.interests"
                                    inputLabelTranslation={`${t('brand.campaign.discovery.categories')}`}
                                    handleChangeParams={changeAutocompleteHandler}
                                    changeEventKey="interests"
                                    setFilterState={setMoodashFilter}
                                    isDisabled={isDiscoveryLimited}
                                />
                                <TooltipOverlay
                                    className="ms-1"
                                    content={t(`brand.campaign.discovery.categories_tooltip`)}
                                >
                                    <FiInfo size={14} />
                                </TooltipOverlay>
                            </div>
                        </Col>
                    )}
                    {[MoodashActivePlatforms.INSTAGRAM, MoodashActivePlatforms.TIKTOK].includes(
                        moodashFilters.platform
                    ) && (
                        <Col lg={3} md={4} className="mt-2">
                            <CustomAutocomplete
                                key={`hashtags-${activePlatform}-${resetKey}`}
                                dataArray={hashtags}
                                defaultArray={uiDefaults?.[activePlatform]?.influencer?.hashtags ?? []}
                                isMultiple={true}
                                isWithImage={false}
                                inputLabel="influencer.hashtags"
                                queryFilterKey="influencer.textTags.hashtags"
                                inputLabelTranslation={`${t('brand.campaign.discovery.hashtags')}`}
                                handleChangeParams={changeAutocompleteHandler}
                                changeEventKey="hashtags"
                                setFilterState={setMoodashFilter}
                                disableClientFilter={true}
                                isDisabled={isDiscoveryLimited}
                            />
                        </Col>
                    )}
                    {[MoodashActivePlatforms.INSTAGRAM, MoodashActivePlatforms.TIKTOK].includes(
                        moodashFilters.platform
                    ) && (
                        <Col lg={3} md={4} className="mt-2">
                            <div className="d-flex align-items-center">
                                <CustomAutocomplete
                                    key={`mentions-${activePlatform}-${resetKey}`}
                                    dataArray={mentions}
                                    defaultArray={uiDefaults?.[activePlatform]?.influencer?.mentions ?? []}
                                    isMultiple={true}
                                    isWithImage={false}
                                    inputLabel="influencer.mentions"
                                    queryFilterKey="influencer.textTags.mentions"
                                    inputLabelTranslation={`${t('brand.campaign.discovery.mentions')}`}
                                    handleChangeParams={changeAutocompleteHandler}
                                    changeEventKey="mentions"
                                    setFilterState={setMoodashFilter}
                                    disableClientFilter={true}
                                    isDisabled={isDiscoveryLimited}
                                />
                                <TooltipOverlay
                                    className="ms-1"
                                    content={t(`brand.campaign.discovery.mentions_tooltip`)}
                                >
                                    <FiInfo size={14} />
                                </TooltipOverlay>
                            </div>
                        </Col>
                    )}
                    {activePlatform && (
                        <Col lg={3} md={4} className="mt-2">
                            <div className="d-flex align-items-center">
                                <CustomAutocomplete
                                    key={`topics-${activePlatform}-${resetKey}`}
                                    dataArray={topics}
                                    defaultArray={uiDefaults?.[activePlatform]?.influencer?.topics ?? []}
                                    isMultiple={true}
                                    isWithImage={false}
                                    inputLabel="influencer.topics"
                                    queryFilterKey="influencer.relevance.topics"
                                    inputLabelTranslation={`${t('brand.campaign.discovery.topics')}`}
                                    handleChangeParams={changeAutocompleteHandler}
                                    changeEventKey="topics"
                                    setFilterState={setMoodashFilter}
                                    isDisabled={isDiscoveryLimited}
                                />
                                <TooltipOverlay className="ms-1" content={t(`brand.campaign.discovery.topics_tooltip`)}>
                                    <FiInfo size={14} />
                                </TooltipOverlay>
                            </div>
                        </Col>
                    )}
                    {[MoodashActivePlatforms.INSTAGRAM, MoodashActivePlatforms.TIKTOK].includes(
                        moodashFilters.platform
                    ) && (
                        <Col className="mt-2" lg={2} md={4}>
                            <div className="d-flex align-items-center">
                                <TextField
                                    label={t('brand.campaign.discovery.bio')}
                                    variant="outlined"
                                    onChange={event => handleFilterChange(event, 'influencer.bio')}
                                    size="small"
                                    value={filters?.[activePlatform]?.influencer?.bio ?? ''}
                                    name="bio"
                                    key={`bio-${resetKey}`}
                                    className="w-100"
                                    disabled={isDiscoveryLimited}
                                />
                                <TooltipOverlay className="ms-1" content={t(`brand.campaign.discovery.bio_tooltip`)}>
                                    <FiInfo size={14} />
                                </TooltipOverlay>
                            </div>
                        </Col>
                    )}
                    {[MoodashActivePlatforms.INSTAGRAM].includes(moodashFilters.platform) && (
                        <Col className="mt-2" lg={2} md={4}>
                            <CustomAutocomplete
                                key={`fake_followers-${activePlatform}-${resetKey}`}
                                dataArray={fakeFollowersArr}
                                defaultArray={
                                    fakeFollowersArr.find(
                                        item => item.value === filters?.[activePlatform]?.audience?.credibility
                                    ) ?? []
                                }
                                isMultiple={false}
                                isWithImage={false}
                                inputLabel="audience.credibility"
                                queryFilterKey="audience.credibility"
                                inputLabelTranslation={`${t('brand.campaign.discovery.fake_followers')}`}
                                handleChangeParams={changeAutocompleteHandler}
                                isDisabled={isDiscoveryLimited}
                            />
                        </Col>
                    )}
                </Row>
            )}
            {/*AUDIENCE FILTERS*/}
            <Row
                onClick={event => {
                    event.stopPropagation();
                    openUpgradeModalAction();
                }}
                className="py-3 mb-2 mt-3 border-top border-bottom"
            >
                <div
                    className={`d-flex justify-content-between align-items-center ${
                        isAudienceFilterShown ? 'mb-3' : ''
                    }`}
                >
                    <h5 className="mb-0">{t('brand.campaign.discovery.audience_filters')}</h5>
                    <Image
                        src="/icons/img-double-down.png"
                        alt="Arrow down img"
                        className={`${isAudienceFilterShown && 'rotate-180'} rotate-smooth cursor-pointer`}
                        height={15}
                        onClick={() => setIsAudienceFilterShown(prevState => !prevState)}
                    />
                </div>
                {isAudienceFilterShown && (
                    <>
                        <Col lg={3} md={4} className="mb-2 mb-lg-0">
                            <div className="d-flex align-items-center">
                                <CustomAutocomplete
                                    key={`audience-location-${activePlatform}-${resetKey}`}
                                    dataArray={audienceLocations}
                                    defaultArray={uiDefaults?.[activePlatform]?.audience?.location ?? []}
                                    isMultiple={true}
                                    isWithImage={false}
                                    inputLabel="discovery.location"
                                    queryFilterKey="audience.location"
                                    inputLabelTranslation={`${t('brand.campaign.discovery.audience_location')}`}
                                    handleChangeParams={changeAutocompleteHandler}
                                    changeEventKey="audienceLocations"
                                    setFilterState={setMoodashFilter}
                                    disableClientFilter={true}
                                    isDisabled={isDiscoveryLimited}
                                />
                                <TooltipOverlay
                                    className="ms-1"
                                    content={t(`brand.campaign.discovery.audience_location_tooltip`)}
                                >
                                    <FiInfo size={14} />
                                </TooltipOverlay>
                            </div>
                        </Col>
                        <Col lg={2} md={4} className="mb-2 mb-md-0">
                            <CustomAutocomplete
                                key={`audience_gender-${activePlatform}-${resetKey}`}
                                dataArray={Object.values(AudienceGender)}
                                defaultArray={
                                    Object.values(AudienceGender).find(
                                        item => item === filters?.[activePlatform]?.audience?.gender?.id
                                    ) ?? []
                                }
                                isMultiple={false}
                                isWithImage={false}
                                inputLabel="moodash_gender"
                                queryFilterKey="audience.gender.id"
                                inputLabelTranslation={`${t('brand.campaign.discovery.gender')}`}
                                handleChangeParams={changeAutocompleteHandler}
                                isDisabled={isDiscoveryLimited}
                            />
                        </Col>
                        {!!filters[activePlatform]?.audience?.gender?.id && (
                            <Col lg={2} md={3} className="mb-2 mb-md-0">
                                <CustomAutocomplete
                                    key={`audience_gender_percentage-${resetKey}`}
                                    dataArray={audiencePercentages}
                                    defaultArray={
                                        audiencePercentages.find(
                                            item => item.value === filters?.[activePlatform]?.audience?.gender?.weight
                                        ) ?? ''
                                    }
                                    isMultiple={false}
                                    isWithImage={false}
                                    inputLabel="audience.credibility"
                                    queryFilterKey="audience.gender.weight"
                                    inputLabelTranslation={`${t('brand.campaign.discovery.gender_percentage')}`}
                                    handleChangeParams={changeAutocompleteHandler}
                                    isDisabled={isDiscoveryLimited}
                                />
                            </Col>
                        )}
                        <Col lg={2} md={4} className="mb-2 mb-md-0">
                            <CustomAutocomplete
                                key={`audience_age-${resetKey}`}
                                dataArray={Object.values(AudienceAge)}
                                defaultArray={uiDefaults?.[activePlatform]?.audience?.age ?? []}
                                isMultiple={true}
                                isWithImage={false}
                                inputLabel="moodash_audience_age"
                                queryFilterKey="audience.age"
                                inputLabelTranslation={`${t('brand.campaign.discovery.audience_age')}`}
                                handleChangeParams={changeAutocompleteHandler}
                                isDisabled={isDiscoveryLimited}
                            />
                        </Col>
                        <Col lg={2} md={4} className="mb-2 mb-md-0">
                            <CustomAutocomplete
                                key={`languages_audience-${activePlatform}-${resetKey}`}
                                dataArray={languages}
                                defaultArray={uiDefaults?.[activePlatform]?.audience?.language?.id ?? ''}
                                isMultiple={false}
                                isWithImage={false}
                                inputLabel="language.name"
                                queryFilterKey="audience.language.id"
                                inputLabelTranslation={`${t('brand.campaign.discovery.language')}`}
                                handleChangeParams={changeAutocompleteHandler}
                                changeEventKey="languages"
                                setFilterState={setMoodashFilter}
                                disableClientFilter={true}
                                isDisabled={isDiscoveryLimited}
                            />
                        </Col>
                        {!!filters[activePlatform]?.audience?.language?.id && (
                            <Col className={`${filters[activePlatform]?.audience?.gender?.id && 'mt-2'}`} lg={2} md={3}>
                                <CustomAutocomplete
                                    key={`audience_age_percentage-${resetKey}`}
                                    dataArray={audiencePercentages}
                                    defaultArray={
                                        audiencePercentages.find(
                                            item => item.value === filters?.[activePlatform]?.audience?.language?.weight
                                        ) ?? ''
                                    }
                                    isMultiple={false}
                                    isWithImage={false}
                                    inputLabel="audience.credibility"
                                    queryFilterKey="audience.language.weight"
                                    inputLabelTranslation={`${t('brand.campaign.discovery.language_percentage')}`}
                                    handleChangeParams={changeAutocompleteHandler}
                                    isDisabled={isDiscoveryLimited}
                                />
                            </Col>
                        )}
                    </>
                )}
            </Row>

            <div className="mb-3 mt-3">
                <h5 className="mb-0">{t('brand.campaign.discovery.search_by_handle')}</h5>
            </div>

            <div className="d-lg-flex justify-content-between align-items-center mb-4 pb-2">
                <CustomAutocomplete
                    dataArray={users}
                    key={`user_mentions-${activePlatform}-${resetKey}`}
                    defaultArray={uiDefaults?.[activePlatform]?.influencer?.usernames ?? []}
                    isMultiple={true}
                    isWithImage={false}
                    inputLabel="influencer.usernames"
                    queryFilterKey="influencer.relevance.usernames"
                    inputLabelTranslation={`${t('brand.campaign.discovery.creator_or_email')}`}
                    handleChangeParams={changeAutocompleteHandler}
                    changeEventKey="users"
                    setFilterState={setMoodashFilter}
                    classes="w-md-30"
                    forbiddenCharacters={/@/g}
                    isDisabled={isDiscoveryLimited}
                />
                <div className="d-flex flex-md-row flex-column align-items-md-center my-lg-0 my-2 ">
                    <p className="mb-lg-0 mx-lg-3 text-muted w-md-fit">
                        {t('brand.campaign.discovery.search_description')}
                    </p>
                    <div className="d-flex align-items-center text-end">
                        <Button
                            disabled={isDiscoveryLimited}
                            className="mr-2"
                            variant="outline-primary"
                            onClick={clearFilterAction}
                        >
                            {t('brand.campaign.discovery.clear_all_filters')}
                        </Button>
                        <Button variant="primary" type="submit">
                            {t('general.button.search')}
                        </Button>
                    </div>
                </div>
            </div>
        </Form>
    );
};

export default DiscoveryFilter;
